import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {List} from 'antd';
import {CarOutlined} from '@ant-design/icons';

import {useGetCarLocationsQuery} from '../../../store/api/carsApi';
import ListCard from '../../../components/ListCard';
import PageTitle from '../../../components/page/PageTitle';
import ErrorBoundary from '../../../components/ErrorBoundary';
import {BaseError} from "../../../types/apiTypes";
import {messageType, showMessages} from '../../../helpers/message';
import {Car} from '../../../types/carTypes';

import './CarsView.less';
import {listGridStyle} from '../../../assets/styles';
import carPlaceholderImage from '../../../assets/car-placeholder.png';

const CarsView = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [page, setPage] = React.useState(location.state && location.state.page ? location.state.page : 1);
    const [perPage, setPerPage] = React.useState(location.state && location.state.perPage ? location.state.perPage : 10);

    const {data: cars, isLoading, error, isError} = useGetCarLocationsQuery({
        page,
        perPage,
    });

    const total = cars?.total || 0;

    React.useEffect(() => {
        if (isError) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [error, isError]);

    const onClick = React.useCallback((id: number) => {
        navigate(`/fleet/car?id=${id}`, {
            state: {page, perPage}
        });
    }, [navigate, page, perPage]);

    React.useEffect(() => {
        const {state} = location;
        if (state && state.page && state.perPage) {
            setPage(state.page);
            setPerPage(state.perPage);
        }
    }, [location]);

    React.useEffect(() => {
        window.history.replaceState(null, '');
    }, []);

    const pageChange = React.useCallback((page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    }, []);

    const renderItem = React.useCallback((car: Car) => {
        const title = car.deployable_description || 'N/A';
        const description = car.location_description || 'No current location.';
        const imageDescription = 'Vehicle #' + car.number;

        return (
            <List.Item key={car.id}>
                <ListCard
                    buttonText="View Vehicle"
                    id={car.id}
                    title={title}
                    description={description}
                    onClick={() => onClick(car.id)}
                    image={car.image ? car.image : carPlaceholderImage}
                    imageAltText="car"
                    imageDescription={imageDescription}
                />
            </List.Item>
        );
    }, [onClick]);

    const paginationAttr = React.useMemo(() => ({
        onChange: pageChange,
        showSizeChanger: true,
        onShowSizeChange: pageChange,
        current: page,
        total,
        pageSize: perPage,
    }), [pageChange, total, perPage, page]);

    return (
        <ErrorBoundary>
            <PageTitle title="All Vehicles" icon={<CarOutlined/>}/>
            <List
                className="list-pagination-bottom cars-list"
                grid={listGridStyle}
                dataSource={cars?.data || []}
                loading={isLoading}
                pagination={paginationAttr}
                renderItem={renderItem}
            />
        </ErrorBoundary>
    );
};

export default CarsView;
